import { EnvironmentConfig } from "@me8eon/config";
import { getFrom } from "../../../util/map";
import { SearchableIndexValue, SourceNameWithIndices } from "../indices/indices.types";
import { getSourceNameByIndex } from "../indices/indices.helpers";

type ExhaustiveObject = Record<SearchableIndexValue | "default", (searchResult: any) => string>;

const addUrlIfPresent = (searchResult: any) => {
    /**
     * @TODO - This is a temporary solution to delete .doc files from search results
     */
    if (searchResult.url && !searchResult.url.includes(".doc")) {
        return `**Matched URL**: ${searchResult.url}`;
    }
    return "";
};

const getDefaultTextForQuestionator = (searchResult: any) => {
    return `**Question**: ${searchResult.keyfield}
    **Answer**: ${searchResult.answer}
    ${addUrlIfPresent(searchResult)}`;
};

const getTextForMyGeniusApiWithContent = (searchResult: any) => {
    return `**Question**: ${searchResult.keyfield}
    **Matching Content**: ${searchResult.content}
    Document link (url): ${searchResult.eon_edcast_link}`;
}

const getDefaultTextForSemantic = (searchResult: any) => {
    return `**Matched question**: """${searchResult.keyfield}"""
    **Matched content**: """${searchResult?.content || ""}"""
   ${addUrlIfPresent(searchResult)}`;
};

const getDefaultTextForServicenow = (searchResult: any) => {
    return searchResult.content
};

const getDefaultTextForStaticQuestionator = (searchResult: any) => searchResult.content

const getDefaultTextForApps = (searchResult: any) => {
    let target = searchResult.target;
    if (target.startsWith('/')) {
        target = EnvironmentConfig.gatewayURL + target;
    }

    return `This is document about an app that the user can use.
    **App Title**: ${searchResult.title || ""}
    **App subtitle**: ${searchResult.subTitle || ""}
    **App Url**: ${target || ""}
    **Description**: ${searchResult.description || ""}
    ${addUrlIfPresent(searchResult)}`;
}

export const getDefaultTextForHr = (searchResult: any): string => {
    return searchResult.content || searchResult.body || searchResult.keyfield || "No policy text available";
};

const textByIndex: ExhaustiveObject = {
    [EnvironmentConfig.appIndex]: (searchResult: any) => {
        let target = searchResult.target;
        if (target.startsWith('/')) {
            target = EnvironmentConfig.gatewayURL + target;
        }

        return `This is document about an app that the user can use.
        App Title: ${searchResult["title"]}
        App Url: ${target}`
    },
    "questionator-office-buddy-dev": (searchResult: any) => getDefaultTextForQuestionator(searchResult),
    "questionator-office-buddy-prod": (searchResult: any) => getDefaultTextForQuestionator(searchResult),
    "questionator-office-buddy-hr-admin-dev": (searchResult: any) => getDefaultTextForQuestionator(searchResult),
    "questionator-office-buddy-hr-admin-prod": (searchResult: any) => getDefaultTextForQuestionator(searchResult),
    "semantic-azureblob-swedish-prod": (searchResult: any) => getDefaultTextForSemantic(searchResult),
    [EnvironmentConfig.semanticOfficeBuddyIndex]: (searchResult: any) => getDefaultTextForQuestionator(searchResult),
    "semantic-edis-confluence-prod": (searchResult: any) => getDefaultTextForSemantic(searchResult),
    "semantic-meateon-confluence-prod": (searchResult: any) => getDefaultTextForSemantic(searchResult),
    "semantic-meeondata-azureblob-prod": (searchResult: any) => getDefaultTextForSemantic(searchResult),
    "questionator-mygenius-api-prod": (searchResult: any) => getTextForMyGeniusApiWithContent(searchResult),
    "semantic-myhr-confluence-prod": (searchResult: any) => getDefaultTextForSemantic(searchResult),
    "semantic-apps-dev": (searchResult: any) => getDefaultTextForApps(searchResult),
    "semantic-apps-prod": (searchResult: any) => getDefaultTextForApps(searchResult),
    "questionator-order-it-api-prod": (searchResult: any) => getDefaultTextForStaticQuestionator(searchResult),
    "questionator-office-buddy-v2": (searchResult: any) => getDefaultTextForQuestionator(searchResult),
    "questionator-mygenius-api-v2": (searchResult: any) => getTextForMyGeniusApiWithContent(searchResult),
    "semantic-meeondata-azureblob-v2": (searchResult: any) => getDefaultTextForSemantic(searchResult),
    "semantic-kb-servicenow-v2": (searchResult: any) => getDefaultTextForSemantic(searchResult),
    "semantic-apps-dev-v2": (searchResult: any) => getDefaultTextForApps(searchResult),
    "questionator-order-it-api-v2": (searchResult: any) => getDefaultTextForStaticQuestionator(searchResult),
    "semantic-kb-servicenow-dev2": (searchResult: any) => getDefaultTextForServicenow(searchResult),
    "semantic-kb-servicenow-prod2": (searchResult: any) => getDefaultTextForServicenow(searchResult),
    "questionator-office-buddy-itsm-dev": (searchResult: any) => getDefaultTextForServicenow(searchResult),
    "questionator-office-buddy-itsm-prod": (searchResult: any) => getDefaultTextForServicenow(searchResult),
    [EnvironmentConfig.semanticHrIndex]: (searchResult: any) => getDefaultTextForHr(searchResult),
    "semantic-confluence-prod": searchResult => getDefaultTextForSemantic(searchResult),
    "semantic-compass-prod-v2": searchResult => getDefaultTextForSemantic(searchResult),
    "ai-sharepoint-dso-dppublic-dev": (searchResult: any) => getDefaultTextForSemantic(searchResult),
    "semantic-confluence-itsm-dev": searchResult => getDefaultTextForSemantic(searchResult),
    // "semantic-confluence-itsm-prod": searchResult => getDefaultTextForSemantic(searchResult),
    "ai-sharepoint-dso-dp-entwicklung": searchResult => getDefaultTextForSemantic(searchResult),
    "ai-sharepoint-dso-dp-betrieb": searchResult => getDefaultTextForSemantic(searchResult),
    "ai-sharepoint-test-team-dso-dp": searchResult => getDefaultTextForSemantic(searchResult),

    default: (searchResult: any) => {
        throw new Error(`There's no document mapping for this search index: ${searchResult.index}`);
    },
};

const mapSearchResultToDocument = (sourceNameWithIndices: SourceNameWithIndices) => (searchResult: any) => {
    const baseText = getFrom(textByIndex)(searchResult.index)(searchResult);
    const sourceName = getSourceNameByIndex(searchResult.index, sourceNameWithIndices);

    return `${baseText}
    
     Document Source name: ${sourceName}`
};

export default mapSearchResultToDocument;